import { METRIC_TO_HEALTH_FIELD, ROLLING_STAT_FIELDS } from './healthMetricMapping';

/**
 * Gets threshold info for a metric from healthSettings.
 *
 * @param {string} metric - Alarm metric name (e.g. 'Latency', 'Packet Loss')
 * @param {object} healthSettings - test.get('config.healthSettings')
 * @param {object} [rollingStats] - Rolling stats from health_ts (e.g. { rolling_avg_latency, rolling_stddev_latency })
 * @returns {{
 *   // Non-stddev mode fields
 *   warning?: number,
 *   critical?: number,
 *   // Stddev mode fields
 *   warningStddev?: number,
 *   criticalStddev?: number,
 *   warningDerived?: number|null,
 *   criticalDerived?: number|null,
 *   // Common fields
 *   unit: string,
 *   isStdDev: boolean,
 *   isInverted: boolean,
 *   isDisabled: boolean,
 *   warningDisplay: string,
 *   criticalDisplay: string
 * } | null}
 */
export function getMetricThresholds(metric, healthSettings, rollingStats) {
  const field = METRIC_TO_HEALTH_FIELD[metric];
  if (!field || !healthSettings) {
    return null;
  }

  const { prefix, unit, divisor = 1, inverted = false, disabledKey, metricUnitKey } = field;
  const isDisabled = disabledKey ? !!healthSettings.disabledMetrics?.[disabledKey] : false;

  const warningRaw = healthSettings[`${prefix}Warning`];
  const criticalRaw = healthSettings[`${prefix}Critical`];
  const warningStddev = healthSettings[`${prefix}WarningStddev`];
  const criticalStddev = healthSettings[`${prefix}CriticalStddev`];

  // StdDev mode: check the metricUnit field first, fall back to checking if absolute values are 0 with stddev values set
  const isStdDev =
    (metricUnitKey && healthSettings[metricUnitKey] === 'stdDev') ||
    (warningRaw === 0 && criticalRaw === 0 && (warningStddev > 0 || criticalStddev > 0));

  if (isStdDev) {
    const rolling = rollingStats ? getRollingStatsForPrefix(prefix, rollingStats) : null;
    return {
      unit,
      isStdDev: true,
      isInverted: inverted,
      isDisabled,
      warningStddev,
      criticalStddev,
      warningDerived: rolling ? derivedThreshold(rolling.avg, rolling.stddev, warningStddev, divisor, inverted) : null,
      criticalDerived: rolling
        ? derivedThreshold(rolling.avg, rolling.stddev, criticalStddev, divisor, inverted)
        : null,
      warningDisplay: formatStddevDisplay(warningStddev, rolling, divisor, inverted, unit),
      criticalDisplay: formatStddevDisplay(criticalStddev, rolling, divisor, inverted, unit)
    };
  }

  return {
    unit,
    isStdDev: false,
    isInverted: inverted,
    isDisabled,
    warning: warningRaw / divisor,
    critical: criticalRaw / divisor,
    warningDisplay: `${warningRaw / divisor}${unit}`,
    criticalDisplay: `${criticalRaw / divisor}${unit}`
  };
}

function formatStddevDisplay(multiplier, rolling, divisor, inverted, unit) {
  if (rolling) {
    const derived = derivedThreshold(rolling.avg, rolling.stddev, multiplier, divisor, inverted);
    return `${multiplier}σ (≈${derived}${unit})`;
  }
  return `${multiplier}σ`;
}

function getRollingStatsForPrefix(prefix, rollingStats) {
  const fields = ROLLING_STAT_FIELDS[prefix];
  if (!fields) {
    return null;
  }

  const avg = rollingStats[fields.avg];
  const stddev = rollingStats[fields.stddev];
  if (avg == null || stddev == null) {
    return null;
  }

  return { avg, stddev };
}

function derivedThreshold(avg, stddev, multiplier, divisor, inverted) {
  const raw = inverted ? avg - multiplier * stddev : avg + multiplier * stddev;
  return Math.round((raw / divisor) * 100) / 100;
}

/**
 * Gets all health thresholds for a test, organized by metric.
 * Used by the config drawer and sidebar to display all thresholds.
 *
 * @param {object} healthSettings - test.get('config.healthSettings')
 * @param {Array<{ metric: string, label: string }>} metricDescriptors - from getMetricsForTest()
 * @returns {Array<{ label: string, metric: string, thresholds: object|null, isDisabled: boolean }>}
 */
export function getAllHealthThresholds(healthSettings, metricDescriptors) {
  if (!healthSettings || !metricDescriptors) {
    return [];
  }

  return metricDescriptors.map(({ metric, label }) => {
    const thresholds = getMetricThresholds(metric, healthSettings);
    return {
      label,
      metric,
      thresholds,
      isDisabled: thresholds?.isDisabled ?? false
    };
  });
}
